// src/modules/orders/components/OrderTable.tsx
import React, { useMemo } from "react";
import { CircularProgress } from "@mui/material";
import { OrderList } from "../types/orderTypes";
import CommonTable, { HeadCell } from "src/business-types/grocery/components/CommonTable";

interface OrderTableProps {
  orders: OrderList[];
  loading: boolean;
  error: string | null;
  page: number;
  rowsPerPage: number;
  totalRecords: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onOrderSelect: (order: OrderList) => void;
}

interface FormattedOrderRow {
  id: string;
  sno: number;
  CustomerName: string;
  Orderid: string;
  table_number: string;
  CustomerPhoneNo: string;
  OrderType: string;
  OrderStatus: JSX.Element | string;
  Ordertime: string;
  Finalamount: string | number;
}

const OrderTable: React.FC<OrderTableProps> = ({ orders, loading, error, page, rowsPerPage, onOrderSelect }) => {
  const headCells: HeadCell<FormattedOrderRow>[] = [
    { id: "sno", label: "S.No" },
    { id: "CustomerName", label: "Customer Name" },
    { id: "Orderid", label: "Order ID" },
    { id: "table_number", label: "Table Number" },
    { id: "CustomerPhoneNo", label: "Phone Number" },
    { id: "OrderType", label: "Order Type" },
    { id: "OrderStatus", label: "Status" },
    { id: "Ordertime", label: "Time" },
    { id: "Finalamount", label: "Total Price", align: "right" },
  ];

  const formattedData = useMemo<FormattedOrderRow[]>(
    () =>
      orders.map((order, index) => ({
        id: order._id.$oid,
        sno: page * rowsPerPage + (index + 1),
        CustomerName: order.CustomerName || "—",
        Orderid: order.Orderid || "—",
        table_number: order.table_number || "—",
        CustomerPhoneNo: order.CustomerPhoneNo || "—",
        OrderType: order.OrderType || "—",
        OrderStatus: (
          <span
            className={`font-bold ${
              order.OrderStatus?.toLowerCase() === "completed"
                ? "text-green-600"
                : order.OrderStatus?.toLowerCase() === "pending"
                ? "text-yellow-600"
                : order.OrderStatus?.toLowerCase() === "cancelled"
                ? "text-red-600"
                : "text-black"
            }`}
          >
            {order.OrderStatus || "Unknown"}
          </span>
        ),

        Ordertime: order.Ordertime || "—",
        Finalamount: order.Finalamount || "—",
      })),
    [orders, page, rowsPerPage] // ✅ include these in dependency list
  );

  if (loading)
    return (
      <div className="flex justify-center items-center py-12">
        <CircularProgress />
        <span className="ml-3 text-gray-600">Loading orders...</span>
      </div>
    );

  if (error)
    return (
      <div className="text-center text-red-500 py-6 bg-red-50 mx-4 rounded-lg">
        <div className="font-semibold">Error Loading Orders</div>
        <div className="text-sm mt-1">{error}</div>
      </div>
    );

  return (
    <div className="m-4">
      <CommonTable<FormattedOrderRow>
        data={formattedData}
        headCells={headCells}
        title=""
        selectable={false}
        showToolbar={false}
        pagenation={false}
        pageSize={rowsPerPage}
        onRowClick={(id) => {
          const selected = orders.find((o) => o._id.$oid === id);
          if (selected) onOrderSelect(selected);
        }}
        className="rounded-lg border w-full border-gray-200"
      />
    </div>
  );
};

export default OrderTable;
